import {
  LIST_STALE_TIME_MS,
  useApiClient,
  useFeatureFlags,
  useIsFeatureFlagActive,
  usePermissions,
} from '@klareospace/pharos-lib-front-utils';
import type { OnboardingWizardInfo } from '@klareospace/pharos-lib-types';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '&/auth';
import { FEATURE_FLAGS } from '&/feature-flags';
import { createOnboardingWizardApi, onboardingWizardKeys } from '../api';

// ─── Constants ────────────────────────────────────────────────────────────────

const ONBOARDING_REQUIRED_PERMISSIONS = [
  'tenants:manage',
  'handling-unit-types:manage',
  'vehicles:manage',
  'users:manage',
];

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseOnboardingGateResult {
  /** `infoQuery.dataUpdatedAt` — changes identity whenever a fresh fetch/refetch lands, 0 while never fetched. */
  dataUpdatedAt: number;
  /** The backend onboarding-wizard-info payload, once successfully fetched. Kept from the last
   * successful fetch across a later refetch error (TanStack Query v5 preserves `data` on refetch
   * error) — see `resolveGateResult`'s refetch-blip handling. */
  info: OnboardingWizardInfo | undefined;
  /** True while a background refetch of onboarding-wizard-info is in flight (info is already defined). */
  isFetching: boolean;
  /** True while the eligibility decision is still being determined. */
  isLoading: boolean;
  /** True once determined that the onboarding wizard should be displayed. */
  shouldShowWizard: boolean;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Encapsulates the onboarding wizard display decision: feature flag, impersonation,
 * required permissions, and the backend onboarding-wizard-info completion state.
 * The info query is only enabled once all upstream gates pass, so it is never fired
 * while impersonating, while the flag is inactive, or while permissions are missing.
 */
export function useOnboardingGate(): UseOnboardingGateResult {
  const { isAuthenticated, isImpersonating, isLoading: isAuthLoading } = useAuth();
  const canCheckEligibility = isAuthenticated && !isImpersonating;

  const isFlagActive = useIsFeatureFlagActive(FEATURE_FLAGS.DSP_TENANT_ONBOARDING, canCheckEligibility);
  const { isLoading: isFlagsLoading } = useFeatureFlags(canCheckEligibility);

  const { hasAllPermissions, isLoading: isPermissionsLoading, isReady: isPermissionsReady } = usePermissions();
  const hasRequiredPermissions = hasAllPermissions(ONBOARDING_REQUIRED_PERMISSIONS);

  const gatesPassed = canCheckEligibility && isFlagActive && isPermissionsReady && hasRequiredPermissions;

  const client = useApiClient('admin-auth');
  const infoQuery: UseQueryResult<OnboardingWizardInfo> = useQuery({
    enabled: gatesPassed,
    queryFn: () => createOnboardingWizardApi(client).getOnboardingWizardInfo(),
    queryKey: onboardingWizardKeys.info(),
    staleTime: LIST_STALE_TIME_MS,
  });

  return resolveGateResult({
    gatesPassed,
    infoQuery,
    isAuthLoading,
    isEligible: canCheckEligibility,
    isFlagsLoading,
    isPermissionsLoading,
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface ResolveGateResultParams {
  gatesPassed: boolean;
  infoQuery: UseQueryResult<OnboardingWizardInfo>;
  isAuthLoading: boolean;
  isEligible: boolean;
  isFlagsLoading: boolean;
  isPermissionsLoading: boolean;
}

function resolveGateResult({
  gatesPassed,
  infoQuery,
  isAuthLoading,
  isEligible,
  isFlagsLoading,
  isPermissionsLoading,
}: ResolveGateResultParams): UseOnboardingGateResult {
  if (isAuthLoading || !isEligible) {
    return { dataUpdatedAt: 0, info: undefined, isFetching: false, isLoading: isAuthLoading, shouldShowWizard: false };
  }

  if (isFlagsLoading || isPermissionsLoading) {
    return { dataUpdatedAt: 0, info: undefined, isFetching: false, isLoading: true, shouldShowWizard: false };
  }

  if (!gatesPassed) {
    return { dataUpdatedAt: 0, info: undefined, isFetching: false, isLoading: false, shouldShowWizard: false };
  }

  const { data, dataUpdatedAt, isError, isFetching } = infoQuery;

  // Initial-load failure — no previously-fetched data to fall back on — stays fail-closed.
  if (isError && data === undefined) {
    return { dataUpdatedAt, info: undefined, isFetching, isLoading: false, shouldShowWizard: false };
  }

  // A REFETCH error (e.g. the active invalidation fired right after a successful step-1 submit)
  // must not eject the user while previously-fetched data is still cached — TanStack Query v5
  // keeps `data` from the last successful fetch on a refetch error, so `data` being defined here
  // means the decision keeps coming from that cached snapshot regardless of `isError`.
  if (data === undefined) {
    return { dataUpdatedAt, info: undefined, isFetching, isLoading: true, shouldShowWizard: false };
  }

  return { dataUpdatedAt, info: data, isFetching, isLoading: false, shouldShowWizard: !data.completed };
}
